/**
 * Status derivation rules SR-1..SR-5 — docs/05-dev-spec.md §4.1.
 *
 * Intent (docs/01-domain-notes.md §2, RU semantics):
 *   «ждёт ссылку»      — есть на ЕПГУ (статусы 6/7), нет на листе ЕВИ  → WAITING_LINK
 *   «получил ссылку»   — есть на листе ЕВИ                             → LINK_RECEIVED
 *   «прошёл ЕВИ»       — появился в FTP (= в unti-applications)        → EVI_PASSED
 *   «отказ»            — статусы ЕПГУ 4, 14 (условный отказ)           → REJECTED / AWAITING_INFO
 *   «нет данных»       — заявка есть, но ни на ЕВИ, ни в FTP, статус из «промежуточных» → NO_DATA
 *
 * CONTRACT (read this before wiring a caller):
 *
 * 1. `deriveStatus` is a PURE function of `StatusInput` — current presence flags only.
 *    It never sees the previously stored status, so *absence* can never be expressed
 *    as an input. Consequence: if a LINK_RECEIVED application leaves the EVI sheet and
 *    is not yet in FTP, the flags {inEpgu:true, epguRawStatus:"7-…", inLatestEvi:false,
 *    inFtp:false} derive WAITING_LINK. That is NOT a downgrade the domain wants
 *    (05 §4.1: "Disappearance from EVI snapshot alone does NOT downgrade status
 *    (rolling queue) — only FTP/re-appearing evidence changes it"; docs/02b §7: the EVI
 *    sheet is a rolling *queue*, not a log — rows leave it when they pass, so absence
 *    is expected and carries no signal).
 *    Callers that hold a stored status MUST wrap the result in `guardRollingQueue`
 *    (below) before persisting. See cases.ts `ROLLING_QUEUE_DOC`.
 *
 * 2. `agreementRejected` is orthogonal (05 §4.1 step 1: "unless agreementRejected flag —
 *    still EVI_PASSED, flag shown separately"). It never changes the derived status;
 *    it is surfaced as its own flag (see ownership.ts, manual-only write).
 *
 * 3. Asymmetry on presence (documented interpretation, 05 §4.1 steps 2 vs 4):
 *    the raw EPGU code is stored on the application (`Application.epguStatusCode`,
 *    "last raw EPGU status") and EPGU snapshots are append-only (docs/02 §8), so
 *    REJECTED / AWAITING_INFO are derived from the stored code even when the row is
 *    absent from the newest export — otherwise absence would downgrade a terminal
 *    decision. WAITING_LINK keeps its explicit "present in EPGU snapshot"
 *    precondition (step 4). See cases.ts cases SR-4/ABSENT.
 *
 * Rule ids: 05 §4.1 numbers five ordered steps; the orchestrator's rule-id union
 * splits them into six ids so every distinct outcome is citable in tests:
 *   SR-1  step 1  in FTP                         → EVI_PASSED
 *   SR-2  step 2a EPGU raw "4"                   → REJECTED
 *   SR-3a step 2b EPGU raw "14"-class            → AWAITING_INFO
 *   SR-3b step 3  present in latest EVI snapshot → LINK_RECEIVED
 *   SR-4  step 4  present in EPGU, 6/7/accepted  → WAITING_LINK
 *   SR-5  step 5  nothing else                   → NO_DATA
 * TODO(orchestrator): 05 §4.1 calls these "SR-1…SR-5" (5 ids) while the task spec
 * mandates 6 ids (SR-3a/SR-3b). Split is chosen so that ids stay in evaluation order;
 * if the spec is ever renumbered, only RULE_ORDER below and the test names change.
 *
 * ADDITIVE (T.U1, v1.9.38, migration 0037) — a SEVENTH id, evaluated FIRST:
 *   SR-6  —      unti_declined fact (cat.2035.university sync) → REJECTED
 * SR-6 is NOT a 05 §4.1 step: it reads an explicitly-passed fact
 * (`untiDeclined`), never a presence flag, and it outranks even SR-1 — a child
 * УНТИ declined is «Отказ» no matter what the FTP feed still lists. Without the
 * top slot, the very next FTP pass (inFtp = true by construction) would
 * re-derive EVI_PASSED and undo the decline; with it, every caller that holds a
 * row passes the fact and the terminal verdict survives every re-derivation.
 * REJECTED keeps its exclusivity story INTACT inside the presence world: only
 * SR-2's raw "4" (and now the sync's own SR-6 fact) may produce it.
 */
/** Raw status codes seen in the sheets (docs/02 §1/§2, `Статус`/`Статус ЕПГУ`). */
const CODE_CLASSES = {
    4: 'REJECTED', //                        4-Отказано в предоставлении услуги.
    14: 'AWAITING_INFO', //                  14-Ожидание дополнительной информации.
    7: 'INTERIM', //                         7-Промежуточные результаты по заявлению.
    6: 'ACCEPTED_FOR_REVIEW', //             6-Заявление принято к рассмотрению.
    // 1-Заявление зарегистрировано. / 10-Заявление отменено. are intentionally NOT
    // classified: 05 §4.1 gives them no mapping (TODO(orchestrator) — see deriveStatus).
};
/**
 * Text fallbacks for rows where the numeric prefix is missing/mangled. Ordered:
 * first match wins, and `Заявление принято` must be tested before generic `принято`.
 * Codes are the documented stable identifier ("число — стабильный идентификатор"
 * docs/02 §1), so this list is deliberately narrow.
 */
const TEXT_CLASSES = [
    [/отказано\s+в\s+предоставлении/i, 'REJECTED'],
    [/ожидани[ея]\s+дополнительн/i, 'AWAITING_INFO'],
    [/промежуточн[а-яё]*\s+результат/i, 'INTERIM'],
    [/принят[а-яё]*\s+к\s+рассмотрени/i, 'ACCEPTED_FOR_REVIEW'],
];
/** Normalise source noise: NBSP variants, surrounding quotes, Excel-style padding. */
function cleanRawStatus(raw) {
    return raw
        .replace(/[\u00A0\u2007\u202F]/g, ' ')
        .replace(/^["'`\s]+|["'`\s]+$/g, '');
}
/**
 * The code is 1-2 digits (docs/02 §1: `"<число>-<текст>"`). A longer leading digit
 * run (a date, a year, an id) is not a status code.
 */
function extractStatusCode(cleaned) {
    const leading = /^(\d{1,2})(?!\d)/.exec(cleaned);
    const leadingCode = leading?.[1];
    if (leadingCode !== undefined)
        return Number(leadingCode);
    if (/^\d+/.test(cleaned))
        return null; // "2024-…", "1234567890 …" → not a status code
    for (const run of cleaned.match(/\d+/g) ?? []) {
        if (run.length <= 2)
            return Number(run);
    }
    return null;
}
/**
 * Classify a raw EPGU status value into the bucket 05 §4.1 speaks about.
 * Tolerant to every string form observed in the sheets: bare "4", bare "14",
 * "4-Отказано в предоставлении услуги.", "7-Промежуточные результаты по заявлению.",
 * with NBSP/quotes/padding and numeric (Excel-coerced) cells.
 *
 * Returns null for values 05 §4.1 does not classify ("1-Заявление зарегистрировано.",
 * "10-Заявление отменено.") — those fall through to SR-5/NO_DATA.
 * TODO(orchestrator): "10-Заявление отменено." (5/1685 rows, docs/02 §1) has no mapping
 * in 05 §4.1 and is NOT doc-01 «отказ» (= 4, 14). Mapped to NO_DATA (no positive
 * evidence) rather than REJECTED so that REJECTED stays exclusive to raw "4" and the
 * warm-up golden table is not perturbed. Revisit if a CANCELLED status is introduced.
 */
export function classifyEpguStatus(raw) {
    if (raw === null || raw === undefined)
        return null;
    const cleaned = cleanRawStatus(typeof raw === 'number' ? String(raw) : raw);
    if (cleaned === '')
        return null;
    const code = extractStatusCode(cleaned);
    const byCode = code === null ? undefined : CODE_CLASSES[code];
    if (byCode !== undefined)
        return byCode;
    if (code !== null)
        return null; // classified numeric prefix we do not know (1, 10, …)
    for (const [pattern, cls] of TEXT_CLASSES) {
        if (pattern.test(cleaned))
            return cls;
    }
    return null;
}
/**
 * Derive the application status per 05 §4.1, steps evaluated in order.
 * See the module contract for the rolling-queue and presence-asymmetry notes.
 */
export function deriveStatus(input) {
    const cls = classifyEpguStatus(input.epguRawStatus);
    const rawShown = JSON.stringify(input.epguRawStatus ?? null);
    // SR-6 — evaluated FIRST: the УНТИ (cat.2035.university) decline fact is a
    // terminal verdict, like SR-2's raw "4", and it must outrank SR-1's FTP
    // presence — see the rule-id note in the module contract. A fact the sync
    // wrote can never be un-seen by a later feed re-deriving EVI_PASSED.
    if (input.untiDeclined === true) {
        return {
            status: 'REJECTED',
            ruleId: 'SR-6',
            reason: 'SR-6: УНТИ (cat.2035.university) синхронизация recorded a declined application for this unti_id/course_id (unti_declined_at) → REJECTED (T.U1; «скрипт синхронизации УНТИ», source UNTI_API)',
        };
    }
    // SR-1 — step 1: FTP presence outranks everything else.
    if (input.inFtp) {
        return {
            status: 'EVI_PASSED',
            ruleId: 'SR-1',
            reason: input.agreementRejected
                ? `SR-1: present in FTP sync; agreementRejected=true is orthogonal — status stays EVI_PASSED, flag shown separately (05 §4.1 step 1)`
                : `SR-1: present in FTP sync (untiId/eviPassedAt) → EVI_PASSED (05 §4.1 step 1)`,
        };
    }
    // SR-2 — step 2a: raw status "4".
    if (cls === 'REJECTED') {
        return {
            status: 'REJECTED',
            ruleId: 'SR-2',
            reason: `SR-2: EPGU raw status ${rawShown} classifies REJECTED → REJECTED (05 §4.1 step 2)`,
        };
    }
    // SR-3a — step 2b: raw status "14"-class (awaiting additional info).
    if (cls === 'AWAITING_INFO') {
        return {
            status: 'AWAITING_INFO',
            ruleId: 'SR-3a',
            reason: `SR-3a: EPGU raw status ${rawShown} classifies AWAITING_INFO (14-class) → AWAITING_INFO (05 §4.1 step 2)`,
        };
    }
    // SR-3b — step 3: present in the latest EVI snapshot.
    if (input.inLatestEvi) {
        return {
            status: 'LINK_RECEIVED',
            ruleId: 'SR-3b',
            reason: `SR-3b: present in latest EVI snapshot (epguRawStatus=${rawShown} carries no earlier verdict) → LINK_RECEIVED (05 §4.1 step 3)`,
        };
    }
    // SR-4 — step 4: present in EPGU with 6/7/"accepted for review".
    if (input.inEpgu && (cls === 'INTERIM' || cls === 'ACCEPTED_FOR_REVIEW')) {
        return {
            status: 'WAITING_LINK',
            ruleId: 'SR-4',
            reason: `SR-4: present in EPGU, raw status ${rawShown} (6/7/accepted for review) → WAITING_LINK (05 §4.1 step 4)`,
        };
    }
    // SR-5 — step 5: no positive evidence anywhere.
    const evidence = input.inEpgu
        ? `present in EPGU but raw status ${rawShown} carries no classified signal (unclassified code, e.g. "1-Заявление зарегистрировано."/"10-Заявление отменено."), absent from EVI and FTP`
        : 'absent from EPGU, EVI and FTP — no positive evidence';
    return {
        status: 'NO_DATA',
        ruleId: 'SR-5',
        reason: `SR-5: ${evidence} → NO_DATA (05 §4.1 step 5)`,
    };
}
/**
 * Progress ladder used ONLY by the rolling-queue guard. REJECTED/AWAITING_INFO are
 * terminal EPGU verdicts and deliberately sit outside the ladder — they are always
 * applied, even onto a higher-ladder status.
 */
const PROGRESS_RANK = {
    NO_DATA: 0,
    WAITING_LINK: 1,
    LINK_RECEIVED: 2,
    EVI_PASSED: 3,
};
function progressRank(status) {
    switch (status) {
        case 'NO_DATA':
        case 'WAITING_LINK':
        case 'LINK_RECEIVED':
        case 'EVI_PASSED':
            return PROGRESS_RANK[status];
        case 'REJECTED':
        case 'AWAITING_INFO':
            return null;
    }
}
/**
 * Rolling-queue guard — apply before persisting a re-derivation (caller A3 holds the
 * stored status; `deriveStatus` itself cannot, see module contract §1).
 *
 * Keeps the stored status when the fresh derivation is lower on the progress ladder
 * AND was produced by a presence-based tail rule (SR-4/SR-5), i.e. the only thing that
 * changed is that the row is no longer in the EVI queue (or in EPGU/FTP) — absence is
 * never evidence. SR-2/SR-3a (a raw "4"/"14" verdict) always pass through: a terminal
 * EPGU decision must reach the applicant's record even if they had already received a
 * link. SR-6 (the УНТИ decline fact) needs no explicit pass-through for the same
 * reason — REJECTED sits outside the progress ladder, so a terminal verdict onto a
 * ladder status is never "a downgrade" and always applies.
 */
export function guardRollingQueue(previous, next) {
    if (previous === null || previous === next.status) {
        return { ...next, suppressed: false };
    }
    if (next.ruleId === 'SR-2' || next.ruleId === 'SR-3a') {
        return { ...next, suppressed: false };
    }
    const prevRank = progressRank(previous);
    const nextRank = progressRank(next.status);
    if (prevRank !== null && nextRank !== null && nextRank < prevRank) {
        return {
            status: previous,
            ruleId: next.ruleId,
            suppressed: true,
            reason: `${next.ruleId} suppressed by rolling queue: only presence evidence was lost (${next.reason}); keeping stored status ${previous} (05 §4.1: disappearance from EVI alone does NOT downgrade)`,
        };
    }
    return { ...next, suppressed: false };
}
/** Evaluation order, for tests and for the docs table in 05 §4.1. SR-6 first — see the module contract. */
export const RULE_ORDER = [
    'SR-6',
    'SR-1',
    'SR-2',
    'SR-3a',
    'SR-3b',
    'SR-4',
    'SR-5',
];
export const ALL_STATUSES = [
    'WAITING_LINK',
    'LINK_RECEIVED',
    'EVI_PASSED',
    'REJECTED',
    'AWAITING_INFO',
    'NO_DATA',
];
/**
 * T.Z44 (owner, 2026-09-14: «проценты заполняемости без Отказа») — the statuses a quota
 * fill never counts. «Отказ» is a decision AGAINST the seat, so counting it either read a
 * venue as fuller than it is or, on a venue whose every application was rejected, lit a
 * full venue out of refusals. The ONE rule, the ONE place: every fill surface reads its
 * base from here (`FILL_COUNTED_STATUSES`) or from the SQL twin that carries the same
 * `<> 'REJECTED'` predicate (`control-dashboard.sql`, `online-pivot.sql`).
 *
 * Deliberately the base is NOT narrowed further (the owner's own words name only «Отказ»):
 * every other status — including WAITING_LINK and NO_DATA — still occupies the pipeline a
 * quota is measured against. Surfaces that count a NARROWER base by their own decision are
 * the ЦА pivot's two «identifiable» statuses (`apps/api/src/ca-distribution/dto.ts`,
 * `COUNTED_STATUSES`, T.W1) — REJECTED was never in that count, which is why T.Z44 did not
 * have to move it.
 */
export const FILL_EXCLUDED_STATUSES = ['REJECTED'];
/** The fill base: {@link ALL_STATUSES} minus {@link FILL_EXCLUDED_STATUSES} (T.Z44). */
export const FILL_COUNTED_STATUSES = ALL_STATUSES.filter((status) => !FILL_EXCLUDED_STATUSES.includes(status));
/**
 * ADDITIVE (v1.9.33, T.Z141) — the TWO «identifiable by venue» statuses a cell of the
 * «Распределение по ЦА» pivot counts (`#11.5`, api wave 5 / T.W1): «ссылка получена» +
 * «ЕВИ пройден». Declared HERE, not in the api, so the three readers of the base can
 * never drift apart: the api's `COUNTED_STATUSES` (the grid's groupBy where) derives
 * from this array, and the web's `cellHref` puts the SAME list into the register link —
 * a cell's «N» opens exactly N rows, which is the bug this constant closes. NARROWER
 * than {@link FILL_COUNTED_STATUSES} on purpose (see the api dto's own note): the pivot
 * is the distribution of the applications that already identified themselves at a
 * venue, not of the whole funnel.
 */
export const CA_COUNTED_STATUSES = ['LINK_RECEIVED', 'EVI_PASSED'];
/**
 * Σ of the fill-counted buckets of a status map (T.Z44) — the numerator every «занято /
 * заполненность» reading uses. `undefined`/`null`/junk cells read as 0, so a partial map
 * (the web's `Partial<StatusCountMap>`) is a legal input.
 */
export function countFillStatuses(counts) {
    let sum = 0;
    for (const status of FILL_COUNTED_STATUSES) {
        const value = counts?.[status];
        if (typeof value === 'number' && Number.isFinite(value) && value > 0)
            sum += Math.round(value);
    }
    return sum;
}
//# sourceMappingURL=rules.js.map